// src/features/users/UsersPage.tsx
import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, CircleOff, PauseCircle, Users as UsersIcon } from 'lucide-react';
import { EmptyState, formatPersianNumber } from '../../app/components/foundation';
import { VaniaContentSkeleton, VaniaLoadingSystem } from '../../app/components/loading';
import { ControlPlaneDataSurface, ControlPlaneMetricStrip, ControlPlanePage, ControlPlaneSection } from '../../app/components/foundation/control-plane';
import { UserCreateDialog, UserTable } from './components';
import { createUser } from './api/users.api';
import { getTenants } from '../tenants/api/tenants.api';
import { useSearchParams } from 'react-router-dom';
import { useUsers } from './hooks/useUsers';
import './styles/users.css';

/**
 * Users Foundation. Same layout pattern as CustomersPage/TenantsPage:
 * a PageContainer shell, a feature-owned header, then feature content
 * below — RTL and Persian throughout, using only existing
 * tokens/primitives (Card, PageHeader) via app/components/foundation.
 *
 * Rows are loaded from the authenticated Super Admin users API.
 */
export function UsersPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [tenantOptions, setTenantOptions] = useState<Array<{ id: number; name: string }>>([]);
  const customersView = searchParams.get('view') === 'customers';
  const { users, meta, loading, error, refetch } = useUsers(page, pageSize, search);
  const totalPages = Math.max(1, Math.ceil(meta.total / pageSize));
  const safePage = Math.min(page, totalPages);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const activeCount = users.filter((user) => user.status === 'active').length;
  const suspendedCount = users.filter((user) => user.status === 'suspended').length;

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  useEffect(() => {
    tableScrollRef.current?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [page, pageSize, search]);

  useEffect(() => {
    if (!createOpen) return;
    setCreateError(null);
    getTenants(1, 50)
      .then((data) => setTenantOptions(data.tenants.map((tenant) => ({ id: tenant.id, name: tenant.name }))))
      .catch(() => setCreateError('خطا در دریافت فهرست تننت‌ها'));
  }, [createOpen]);

  async function handleCreateUser(payload: Parameters<typeof createUser>[0]) {
    setCreateSubmitting(true);
    setCreateError(null);
    try {
      await createUser(payload);
      setCreateOpen(false);
      await refetch();
    } catch (requestError) {
      const record = requestError && typeof requestError === 'object' ? requestError as Record<string, unknown> : {};
      const body = record.body && typeof record.body === 'object' ? record.body as Record<string, unknown> : {};
      const detail = typeof body.detail === 'string' ? body.detail : '';
      setCreateError(detail === 'TELEGRAM_ID_EXISTS' ? 'این شناسه تلگرام قبلاً ثبت شده است.' : 'ایجاد کاربر انجام نشد.');
    } finally {
      setCreateSubmitting(false);
    }
  }

  return (
    <>
      <ControlPlanePage
      className="vania-users-control-plane-page"
      title=""
      summary={(
        <ControlPlaneMetricStrip
          items={[
            { label: 'کل کاربران', value: formatPersianNumber(meta.total), icon: <UsersIcon size={16} />, tone: 'primary' },
            { label: 'کاربران فعال', value: formatPersianNumber(activeCount), icon: <CheckCircle2 size={16} />, tone: 'success' },
            { label: 'کاربران غیرفعال', value: '۰', icon: <CircleOff size={16} />, tone: 'danger' },
            { label: 'کاربران تعلیق‌شده', value: formatPersianNumber(suspendedCount), icon: <PauseCircle size={16} />, tone: 'warning' },
          ]}
        />
      )}
    >
        <ControlPlaneSection>
          <ControlPlaneDataSurface>
            {error && users.length === 0 ? <p className="vania-async-state vania-async-state-error" role="alert">{error}</p> : null}
            <div className="vania-users-toolbar">
              <label className="vania-users-search">
                <input
                  type="search"
                  value={search}
                  onChange={(event) => { setSearch(event.target.value); setPage(1); }}
                  placeholder="نام، نام کاربری، شناسه تلگرام یا ایمیل"
                  aria-label="جست‌وجوی کاربر"
                />
              </label>
              <div className="vania-users-tabs" role="tablist" aria-label="نمایش کاربران">
                <button type="button" aria-selected={!customersView} onClick={() => { setSearchParams({}); setPage(1); }}>همه کاربران</button>
                <button type="button" aria-selected={customersView} onClick={() => { setSearchParams({ view: 'customers' }); setPage(1); }}>مشتریان</button>
              </div>
              <button
                type="button"
                className="vania-users-tabs__button"
                onClick={() => setCreateOpen(true)}
              >
                افزودن کاربر
              </button>
            </div>
            <div className="vania-users-table-region">
              {error && users.length === 0 ? null : !loading && users.length === 0 ? <EmptyState message="کاربری برای نمایش وجود ندارد." /> : loading && users.length === 0 ? <UsersTableSkeleton /> : <UserTable users={users} scrollRef={tableScrollRef} />}
            </div>
            <div className="vania-users-pagination-bar">
              <label className="vania-users-page-size"><span>تعداد در صفحه</span><select value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }} aria-label="تعداد کاربر در هر صفحه"><option value="20">۲۰</option><option value="25">۲۵</option><option value="50">۵۰</option><option value="100">۱۰۰</option></select></label>
              <nav className="vania-users-pagination" aria-label="صفحه‌بندی کاربران">
                <button type="button" className="vania-users-pagination__control" disabled={safePage === 1} onClick={() => setPage((current) => Math.max(1, current - 1))}>‹</button>
                <div className="vania-users-pagination__pages">{Array.from({ length: totalPages }, (_, index) => index + 1).map((item) => <button type="button" key={item} className={`vania-users-pagination__page${item === safePage ? ' is-active' : ''}`} aria-current={item === safePage ? 'page' : undefined} onClick={() => setPage(item)}>{formatPersianNumber(item)}</button>)}</div>
                <button type="button" className="vania-users-pagination__control" disabled={safePage === totalPages} onClick={() => setPage((current) => Math.min(totalPages, current + 1))}>›</button>
              </nav>
            </div>
          </ControlPlaneDataSurface>
        </ControlPlaneSection>
      </ControlPlanePage>
      <UserCreateDialog
        open={createOpen}
        tenants={tenantOptions}
        submitting={createSubmitting}
        error={createError}
        onClose={() => { if (!createSubmitting) setCreateOpen(false); }}
        onSubmit={handleCreateUser}
      />
    </>
  );
}

function UsersTableSkeleton() {
  return (
    <VaniaLoadingSystem mode="content" label="در حال بارگذاری کاربران">
      <div className="vania-users-table-scroll" aria-hidden="true">
        <table className="vania-users-table">
          <thead>
            <tr>
              {['کاربر', 'Telegram', 'تننت', 'سرویس', 'سفارش', 'موجودی', 'وضعیت', 'تاریخ ایجاد', 'عملیات'].map((heading) => <th key={heading}>{heading}</th>)}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 20 }, (_, rowIndex) => (
              <tr key={`user-skeleton-${rowIndex}`}>
                {Array.from({ length: 9 }, (_, cellIndex) => (
                  <td key={`user-skeleton-${rowIndex}-${cellIndex}`}><VaniaContentSkeleton style={{ width: cellIndex === 0 ? '70%' : '55%' }} /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </VaniaLoadingSystem>
  );
}
